import { RegistroDto, UpdateRegistroDto } from "@/api";
import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
    FormHelperText,
    Grid,
    InputAdornment,
    Stack,
    Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import { isNil, keyBy } from "lodash";
import {
    Control,
    Controller,
    FieldErrors,
    UseFormGetValues,
    UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export type Registri_PRAEVIDERE_GRADUS_35 = {
  measTempTh1?: number;
  measTempTh2?: number;
};

export function registriToForm_PRAEVIDERE_GRADUS_35(_registri: RegistroDto[]): {
  [r in keyof Registri_PRAEVIDERE_GRADUS_35]: any;
} {
  const registri = keyBy(_registri, "registro");

  return {
    measTempTh1: isNil(registri["measTempTh1"])
      ? undefined
      : parseFloat(registri["measTempTh1"].value),
    measTempTh2: isNil(registri["measTempTh2"])
      ? undefined
      : parseFloat(registri["measTempTh2"].value),
  };
}

export function formToRegistri_PRAEVIDERE_GRADUS_35(
  form: Registri_PRAEVIDERE_GRADUS_35
): UpdateRegistroDto[] {
  const topicPrefix = "var";
  const _values: {
    [k in keyof Registri_PRAEVIDERE_GRADUS_35]?: UpdateRegistroDto;
  } = {
    measTempTh1: isNil(form.measTempTh1)
      ? undefined
      : {
          registro: "measTempTh1",
          topicPrefix,
          value: form.measTempTh1,
        },
    measTempTh2: isNil(form.measTempTh2)
      ? undefined
      : {
          registro: "measTempTh2",
          topicPrefix,
          value: form.measTempTh2,
        },
  };
  return Object.values(_values);
}

export function EditorRegistri_PRAEVIDERE_GRADUS_35({
  control,
  errors,
  register,
  getValues,
  readOnly = false,
}: {
  control: Control<Registri_PRAEVIDERE_GRADUS_35>;
  errors: FieldErrors<Registri_PRAEVIDERE_GRADUS_35>;
  register: UseFormRegister<Registri_PRAEVIDERE_GRADUS_35>;
  getValues: UseFormGetValues<Registri_PRAEVIDERE_GRADUS_35>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("praevidereGradus35.sezioni.soglieTemperatura")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("praevidereGradus35.campi.soglia1Temperatura")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measTempTh1"
                    rules={{
                      min: {
                        value: -20,
                        message: t("praevidereGradus35.errori.intervallo", {
                          min: -20,
                          max: 150,
                        }),
                      },
                      max: {
                        value: 150,
                        message: t("praevidereGradus35.errori.intervallo", {
                          min: -20,
                          max: 150,
                        }),
                      },
                      validate: (measTempTh1: number | undefined) => {
                        const { measTempTh2 } = getValues();
                        if (
                          measTempTh1 &&
                          measTempTh2 &&
                          measTempTh1 > measTempTh2
                        ) {
                          return t(
                            "praevidereGradus35.errori.soglia1SuperaSoglia2"
                          );
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">
                              {t("unita.celsius")}
                            </InputAdornment>
                          }
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6} />

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("praevidereGradus35.campi.soglia2Temperatura")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measTempTh2"
                    rules={{
                      min: {
                        value: -20,
                        message: t("praevidereGradus35.errori.intervallo", {
                          min: -20,
                          max: 150,
                        }),
                      },
                      max: {
                        value: 150,
                        message: t("praevidereGradus35.errori.intervallo", {
                          min: -20,
                          max: 150,
                        }),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">
                              {t("unita.celsius")}
                            </InputAdornment>
                          }
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6} />
            </Grid>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
